import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import {
  Search, Eye, Download, FileText, Calendar, 
  CheckCircle, DollarSign, X, RefreshCw, Filter, AlertCircle
} from 'lucide-react';
import MobileInvoicePreview from './MobileInvoicePreview';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import PlanBadge from '../common/PlanBadge';

const MobilePayments = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [highlightInvoiceId, setHighlightInvoiceId] = useState(null);
  const lastHighlightRef = useRef(null);

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (highlightParam) {
      if (lastHighlightRef.current !== highlightParam) {
        lastHighlightRef.current = highlightParam;
        setHighlightInvoiceId(highlightParam);
      }
    } else {
      lastHighlightRef.current = null;
      setHighlightInvoiceId(null);
    }
  }, [searchParams]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const response = await api.get(`/invoices/paid?${params.toString()}`);
      if (response.data.success) {
        const paidInvoices = response.data.data.invoices
          .filter(p => p.status === 'paid')
          .map(invoice => ({
            ...invoice,
            lineItems: invoice.items || invoice.lineItems || [], // InvoicePreview expects lineItems
            attachments: invoice.metadata?.attachments || [] // Attachments from metadata
            // Backend already provides customer.type structure:
            // - customer.type === 'customer' for sent invoices (user is sender)
            // - customer.type === 'supplier' for received invoices (user is receiver)
          }));
        setPayments(paidInvoices);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (payment, extraOptions = {}) => {
    if (!payment) {
      alert('No invoice selected');
      return;
    }

    try {
      await downloadInvoice(
        payment,
        'pdf',
        { setLoading, ...extraOptions }
      );
    } catch (error) {
      console.warn('Mobile payments API download failed, using legacy renderer', error);
      await legacyHandleDownload(payment);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.invoiceNumber?.toLowerCase().includes(query) ||
      payment.sender?.companyName?.toLowerCase().includes(query) ||
      payment.receiver?.companyName?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    // Database stores amounts in pounds as DECIMAL(10,2)
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB');
  };

  // Refund modal handlers
  const handleOpenRefundModal = (payment) => {
    console.log('💰 Opening refund modal for:', payment.invoiceNumber);
    setRefundInvoice(payment);
    setRefundType('full');
    setRefundAmount(payment.amount || payment.total);
    setRefundReason('');
    setShowRefundModal(true);
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setRefundInvoice(null);
    setRefundType('full');
    setRefundAmount('');
    setRefundReason('');
  };

  const calculateRemainingBalance = (payment) => {
    const totalPaid = parseFloat(payment.amount || payment.total || 0);
    const alreadyRefunded = parseFloat(payment.refundAmount || 0);
    return totalPaid - alreadyRefunded;
  };

  const handleSubmitRefund = async () => {
    try {
      // Validation
      if (!refundReason.trim()) {
        alert('Please provide a reason for the refund');
        return;
      }

      if (refundType === 'partial') {
        const refundAmountNum = parseFloat(refundAmount);
        const totalPaid = parseFloat(refundInvoice.amount || refundInvoice.total);
        const alreadyRefunded = parseFloat(refundInvoice.refundAmount || 0);
        const remainingBalance = totalPaid - alreadyRefunded;

        if (isNaN(refundAmountNum) || refundAmountNum <= 0) {
          alert('Please enter a valid refund amount greater than £0');
          return;
        }

        if (refundAmountNum > remainingBalance) {
          alert(`Cannot refund £${refundAmountNum.toFixed(2)}. Remaining balance: £${remainingBalance.toFixed(2)}`);
          return;
        }
      }

      setRefundLoading(true);
      console.log('💰 Submitting refund:', {
        invoiceId: refundInvoice.id,
        refundAmount: refundType === 'full' ? (refundInvoice.amount || refundInvoice.total) : refundAmount,
        refundReason,
        refundType
      });

      try {
        // Amount is already in pounds (DECIMAL), no conversion needed
        const fullRefundPounds = parseFloat(refundInvoice.amount || refundInvoice.total);
        const partialRefundPounds = parseFloat(refundAmount);
        
        const refundData = {
          refundAmount: refundType === 'full' ? fullRefundPounds : partialRefundPounds,
          refundReason: refundReason.trim(),
          refundType: refundType
        };

        const response = await api.post(`/api/invoices/${refundInvoice.id}/refund`, refundData);

        if (response.data.success) {
          console.log('✅ Refund processed successfully:', response.data);
          
          // Show success message
          const message = refundType === 'full' 
            ? `Full refund of ${formatCurrency(refundInvoice.amount || refundInvoice.total)} processed successfully. Customer will receive funds within 5-10 business days.`
            : `Partial refund of ${formatCurrency(refundAmount)} processed successfully. Customer will receive funds within 5-10 business days.`;
          
          alert(message);

          // Close modal
          handleCloseRefundModal();

          // Refresh payments list
          await fetchPayments();
        } else {
          alert('Refund failed: ' + (response.data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('❌ Refund error:', error);
        
        // If we get a 403 error, try direct API Gateway as fallback
        if (error.response?.status === 403 && error.response?.data?.message?.includes('Invalid key-value pair')) {
          console.log('🔄 Proxy failed, trying direct API Gateway...');
          try {
            const token = localStorage.getItem('token');
            const directResponse = await fetch(
              `https://5e460pfqz0.execute-api.eu-west-2.amazonaws.com/prod/api/invoices/${refundInvoice.id}/refund`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  refundAmount: refundType === 'full' ? parseFloat(refundInvoice.amount || refundInvoice.total) : parseFloat(refundAmount),
                  refundReason: refundReason.trim(),
                  refundType: refundType
                })
              }
            );

            const directData = await directResponse.json();
            
            if (directResponse.ok && directData.success) {
              console.log('✅ Refund processed via direct API Gateway:', directData);
              
              const message = refundType === 'full' 
                ? `Full refund of ${formatCurrency(refundInvoice.amount || refundInvoice.total)} processed successfully. Customer will receive funds within 5-10 business days.`
                : `Partial refund of ${formatCurrency(refundAmount)} processed successfully. Customer will receive funds within 5-10 business days.`;
              
              alert(message);
              handleCloseRefundModal();
              await fetchPayments();
            } else {
              throw new Error(directData.error || directData.message || 'Refund failed');
            }
          } catch (fallbackError) {
            console.error('❌ Direct API Gateway also failed:', fallbackError);
            alert('Failed to process refund: ' + (fallbackError.message || 'Unknown error'));
          }
        } else {
          alert('Failed to process refund: ' + (error.response?.data?.message || error.message));
        }
      } finally {
        setRefundLoading(false);
      }
    } catch (error) {
      console.error('❌ Outer refund error:', error);
      setRefundLoading(false);
    }
  };

  const buildFullInvoiceData = async (invoice) => {
    let fullInvoiceData = invoice;

    try {
      const fullInvoiceResponse = await api.get(`/invoices/${invoice.id}`);
      const fullInvoice = fullInvoiceResponse.data.invoice || fullInvoiceResponse.data.data?.invoice || fullInvoiceResponse.data;

      fullInvoiceData = {
        ...invoice,
        ...fullInvoice,
        lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || [],
        sender: fullInvoice.sender || invoice.sender,
        receiver: fullInvoice.receiver || invoice.receiver,
        guestRecipient: fullInvoice.guestRecipient || invoice.guestRecipient || null,
        metadata: fullInvoice.metadata || invoice.metadata || {},
        attachments: fullInvoice.metadata?.attachments || invoice.metadata?.attachments || [],
        refundAmount: fullInvoice.refundAmount || invoice.refundAmount,
        refundStatus: fullInvoice.refundStatus || invoice.refundStatus,
        refundDate: fullInvoice.refundDate || invoice.refundDate,
        discountAmount: fullInvoice.discountAmount || invoice.discountAmount,
        discountSourceInvoice: fullInvoice.discountSourceInvoice || invoice.discountSourceInvoice,
        tax: fullInvoice.tax || invoice.tax,
        subtotal: fullInvoice.subtotal || invoice.subtotal
      };
    } catch (fetchError) {
      console.error('⚠️ Could not fetch full invoice data, using basic payment data:', fetchError);
    }

    return fullInvoiceData;
  };

  const openPaymentPreview = async (payment) => {
    if (!payment) {
      return;
    }

    try {
      setPreviewLoading(true);
      setSelectedInvoice(payment);
      setShowPreview(true);

      const previewData = await buildFullInvoiceData(payment);
      setSelectedInvoice(previewData);
    } catch (error) {
      console.error('Error opening invoice preview:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Unable to access invoice details. You may not have permission to view this invoice.');
      } else {
        alert('Unable to open invoice preview. Please try again.');
      }
      setShowPreview(false);
      setSelectedInvoice(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const legacyHandleDownload = async (payment) => {
    if (!payment) {
      alert('No invoice selected');
      return;
    }
    
    try {
      console.log('📥 Downloading invoice:', payment.invoiceNumber);
      
      const fullInvoiceData = await buildFullInvoiceData(payment);
      
      // Generate PDF with complete data
      generateInvoicePDF(fullInvoiceData, payment.invoiceNumber || 'invoice');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(error.message || 'Failed to download invoice. Please try again.');
    }
  };

  useEffect(() => {
    if (!highlightInvoiceId || payments.length === 0) {
      return;
    }

    const highlightValue = highlightInvoiceId.startsWith('number:')
      ? highlightInvoiceId.slice(7)
      : highlightInvoiceId;
    const matchByNumberOnly = highlightInvoiceId.startsWith('number:');

    const targetInvoice = payments.find((payment) => {
      const identifiers = [
        payment.id,
        payment.invoiceId,
        payment.invoiceNumber
      ]
        .filter(Boolean)
        .map((val) => String(val));

      if (matchByNumberOnly) {
        return identifiers.includes(highlightValue);
      }
      return identifiers.includes(highlightValue);
    });

    if (targetInvoice) {
      openPaymentPreview(targetInvoice);
      setHighlightInvoiceId(null);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('highlight');
      setSearchParams(newParams, { replace: true });
    }
  }, [highlightInvoiceId, payments, searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Payment History</h1>
          <button
            onClick={fetchPayments}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${showFilters ? 'bg-[#1F11ED] text-white' : 'text-gray-600'}`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* Date Filters */}
        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">From Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">To Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setDateRange({ start: '', end: '' });
                  fetchPayments();
                }}
                className="flex-1 text-sm text-gray-600 py-2"
              >
                Clear
              </button>
              <button
                onClick={fetchPayments}
                className="flex-1 bg-[#1F11ED] text-white py-2 rounded-lg text-sm"
              >
                Apply
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Payments List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
            <p className="text-sm text-gray-600">
              {searchQuery ? 'Try adjusting your search' : 'No payment history yet'}
            </p>
          </div>
        ) : (
          filteredPayments.map((payment, index) => (
            <motion.div
              key={payment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{payment.invoiceNumber}</h3>
                    <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3" />
                      <span>Paid</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-600">
                      {payment.sender?.companyName || payment.sender?.name || 
                       payment.receiver?.companyName || payment.receiver?.name || 
                       payment.customer?.companyName || payment.customer?.name || 
                       'Unknown Company'}
                    </p>
                    {/* Show Plan Badge */}
                    {(payment.sender?.subscriptionPlan || payment.receiver?.subscriptionPlan) && (
                      <PlanBadge plan={payment.sender?.subscriptionPlan || payment.receiver?.subscriptionPlan} />
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    £{parseFloat(payment.total || payment.amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                <div>
                  <span className="text-gray-500">Paid Date:</span>
                  <p className="font-medium text-gray-900">{formatDate(payment.paymentDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <p className="font-medium text-gray-900">{formatDate(payment.dueDate)}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openPaymentPreview(payment)}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">View</span>
                </button>
                <button
                  onClick={() => handleDownload(payment)}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Download</span>
                </button>
                
                {/* Refund Button - Only for fully paid invoices from sent invoices (matching desktop) */}
                {payment.customer?.type === 'customer' && !payment.refundStatus && (
                  <button
                    onClick={() => handleOpenRefundModal(payment)}
                    className="p-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                    title="Refund Invoice"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Preview Modal */}
      <MobileInvoicePreview 
        invoice={selectedInvoice}
        isOpen={showPreview}
        loading={previewLoading}
        onClose={() => {
          setShowPreview(false);
          setSelectedInvoice(null);
          setPreviewLoading(false);
        }}
        onDownloadPDF={() => handleDownload(selectedInvoice, { forcePrintablePreview: true })}
        title="Payment Details"
      />

      {/* Refund Modal */}
      {showRefundModal && refundInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Refund Invoice</h2>
                <p className="text-sm text-gray-600 mt-1">Process a full or partial refund for this payment</p>
              </div>
              <button
                onClick={handleCloseRefundModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                disabled={refundLoading}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Invoice Summary Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Invoice Number</p>
                    <p className="text-lg font-bold text-gray-900">{refundInvoice.invoiceNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Amount Paid</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(refundInvoice.amount || refundInvoice.total)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Customer</p>
                    <p className="text-sm font-medium text-gray-900">{refundInvoice.customer?.name || 'Unknown'}</p>
                    <p className="text-xs text-gray-600">{refundInvoice.customer?.email || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Payment Date</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(refundInvoice.paymentDate)}</p>
                  </div>
                  {refundInvoice.refundAmount > 0 && (
                    <>
                      <div>
                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">Already Refunded</p>
                        <p className="text-lg font-bold text-orange-700">{formatCurrency(refundInvoice.refundAmount)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Remaining Balance</p>
                        <p className="text-lg font-bold text-green-700">{formatCurrency(calculateRemainingBalance(refundInvoice))}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Refund Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Refund Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRefundType('full');
                      setRefundAmount(calculateRemainingBalance(refundInvoice).toString());
                    }}
                    className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                      refundType === 'full'
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    disabled={refundLoading}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">Full Refund</p>
                      <p className="text-2xl font-bold text-blue-600 mt-2">
                        {formatCurrency(calculateRemainingBalance(refundInvoice))}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Refund entire remaining amount</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRefundType('partial')}
                    className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                      refundType === 'partial'
                        ? 'border-orange-500 bg-orange-50 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    disabled={refundLoading}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">Partial Refund</p>
                      <p className="text-2xl font-bold text-orange-600 mt-2">Custom</p>
                      <p className="text-xs text-gray-600 mt-1">Refund specific amount</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Partial Refund Amount Input */}
              {refundType === 'partial' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Refund Amount
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      (Max: {formatCurrency(calculateRemainingBalance(refundInvoice))})
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={calculateRemainingBalance(refundInvoice)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg font-semibold"
                      placeholder="0.00"
                      disabled={refundLoading}
                    />
                  </div>
                </div>
              )}

              {/* Refund Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Refund Reason
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Please provide a detailed reason for this refund (e.g., 'Product defect', 'Service not delivered', 'Customer request')..."
                  disabled={refundLoading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  This reason will be saved for audit purposes and shared with the customer.
                </p>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Important Information</p>
                    <ul className="text-xs text-yellow-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Refunds are processed through Stripe and cannot be reversed</li>
                      <li>Funds will appear in customer's account within 5-10 business days</li>
                      <li>The customer will receive an automatic notification</li>
                      <li>Stripe processing fees are automatically refunded</li>
                      <li>Recommended to process refunds within 90 days of payment</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleCloseRefundModal}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-semibold"
                  disabled={refundLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRefund}
                  disabled={!refundReason.trim() || refundLoading || (refundType === 'partial' && (!refundAmount || parseFloat(refundAmount) <= 0))}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {refundLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing Refund...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>
                        Process {refundType === 'full' ? 'Full' : 'Partial'} Refund
                        {refundType === 'full' && ` (${formatCurrency(calculateRemainingBalance(refundInvoice))})`}
                        {refundType === 'partial' && refundAmount && ` (${formatCurrency(refundAmount)})`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePayments;

